import { useEffect, useState } from 'react';
import { useHub } from '../context';
import { DAMAGE_COLORS, ELEMENTS, type D2Index, type D2Item } from '../d2info';
import { readBuilds, readMeta, runMeta, type Build, type BuildsResult, type MetaResult, type Plug, type Side } from '../meta';
import { Asking, Bar, Chip, Pic, SmallButton } from '../ui';
import { ItemCard } from './D2InfoTab';

/** D2 Info's PvP Meta, PvE Meta and Meta Builds (port of the app's ShowMeta and ShowBuilds). */

// one sampler run for the whole page: it keeps going while the visitor moves between the sections
let busy: string | null = null;
let stamp = 0;
const listeners = new Set<() => void>();
const ping = () => { stamp++; listeners.forEach((l) => l()); };
let stopRun = false;

function useMetaState() {
  const [, set] = useState(0);
  const [meta, setMeta] = useState<MetaResult | null>();
  const [builds, setBuilds] = useState<BuildsResult | null>();
  useEffect(() => {
    const l = () => set((x) => x + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  useEffect(() => { void readMeta().then((m) => setMeta(m ?? null)); void readBuilds().then((b) => setBuilds(b ?? null)); }, [stamp]);
  return { meta, builds };
}

function start(me: Parameters<typeof runMeta>[0]) {
  if (busy) return;
  busy = 'Collecting recent matches from Bungie... the first time takes a few minutes; you can keep using the site.';
  stopRun = false;
  ping();
  runMeta(me, (t) => { busy = t; ping(); }, () => stopRun)
    .catch(() => { busy = null; })
    .finally(() => { busy = null; ping(); });
}

export default function MetaPages({ ix, side, openItem }: { ix: D2Index; side: 'pvp' | 'pve' | 'builds'; openItem: (it: D2Item) => void }) {
  const h = useHub();
  const { meta, builds } = useMetaState();
  const me = h.hub?.player;
  useEffect(() => {
    // nothing counted yet: start at once, as the app does
    if (meta === null && me && !busy) start(me);
  }, [meta, me]);
  if (meta === undefined) return <Asking text="Reading the meta..." />;
  const refresh = (
    <SmallButton disabled={!!busy || !me} onClick={() => me && start(me)} title="Collect more recent matches and count again (runs in the background)">
      {side === 'builds' ? 'Refresh' : 'Refresh the meta'}
    </SmallButton>
  );
  const busyLine = busy && <p className="d-busy">{busy} {<button type="button" className="d-linkbtn" onClick={() => { stopRun = true; }}>Stop</button>}</p>;
  if (side === 'builds') return <Builds builds={builds ?? null} ix={ix} openItem={openItem} refresh={refresh} busyLine={busyLine} />;

  const r = meta?.sides[side as Side];
  const top = r?.top ?? [];
  const top1 = Math.max(0.01, top[0]?.usage ?? 0);
  return (
    <>
      <div className="d-metahead">
        <div>
          <div className="d-bighead">{side === 'pvp' ? 'PvP META WEAPONS' : 'PvE META WEAPONS'}</div>
          <p className="d-note">
            {r ? `Ranked by how many players used each weapon in ${r.matches} recent ${side === 'pvp' ? 'Crucible, Trials and Iron Banner' : 'raid, dungeon and Nightfall'} matches (${r.players} players, last 14 days). Updated ${new Date(meta!.updated).toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' })}.`
              : 'No matches collected yet.'}
          </p>
        </div>
        {refresh}
      </div>
      {busyLine}
      {top.map((t, i) => {
        const it = ix.byHash.get(t.hash);
        if (!it) return null;
        const rank = i + 1;
        const tier = rank <= 5 ? 'S' : rank <= 15 ? 'A' : rank <= 30 ? 'B' : 'C';
        const trend = t.trend;
        return (
          <button type="button" key={t.hash} className="d-metarow" onClick={() => openItem(it)}>
            <span className={`tier t${tier}`}><b>{tier}</b><span>#{rank}</span></span>
            <span className="ic" style={{ background: it.tier === 6 ? '#CEAE33' : '#7A4FB0' }}><Pic src={it.icon} lazy />{it.mark && <Pic src={it.mark} lazy />}</span>
            <span className="mid">
              <b>{it.name}</b>
              <span style={{ color: DAMAGE_COLORS[it.damage] }}>{[it.type, t.frame, ELEMENTS[it.damage] ?? ''].filter(Boolean).join('   |   ')}</span>
              <span className="roll">
                {it.tier === 6 ? <span className="d-gold">Exotic: fixed perks</span> : t.godRoll.length ? (
                  <><b className="d-gold">GOD ROLL</b>{t.godRoll.map((p) => <span key={p.name} className="d-minichip"><Pic src={p.icon} width={16} height={16} />{p.name}</span>)}</>
                ) : <span className="d-dim">No community god roll listed</span>}
              </span>
              <span className="src">Get it: {it.source || 'not listed by Bungie'}</span>
            </span>
            <span className="use">
              <b>{t.usage.toFixed(1)} % of players</b>
              <Bar fraction={t.usage / top1} height={6} />
              <span className={trend === null ? '' : trend >= 0.05 ? 'up' : trend <= -0.05 ? 'down' : ''}>
                {trend === null ? '' : trend >= 0.05 ? `+${trend.toFixed(1)} % vs last week   |   ` : trend <= -0.05 ? `${trend.toFixed(1)} % vs last week   |   ` : 'steady   |   '}
                {t.kills.toLocaleString()} kills
              </span>
            </span>
          </button>
        );
      })}
      {top.length > 0 && <p className="d-note">Tiers are by usage rank (S = top 5, A = 6-15, B = 16-30, C = 31-40). Counted from Bungie match reports, starting from your own matches and spreading to the players in them, so it leans towards your skill bracket and region. God rolls from the community DIM wish list.</p>}
    </>
  );
}

function PlugPill({ d }: { d: Plug }) {
  return <span className="d-plug" title={`${d.name}\n${d.type}`}>{d.icon && <Pic src={d.icon} width={22} height={22} />}<span>{d.name}</span></span>;
}

let buildSide: Side = 'pvp';

function Builds({ builds, ix, openItem, refresh, busyLine }: { builds: BuildsResult | null; ix: D2Index; openItem: (it: D2Item) => void; refresh: React.ReactNode; busyLine: React.ReactNode }) {
  const [side, setSide] = useState<Side>(buildSide);
  const [open, setOpen] = useState<string>();
  useEffect(() => { buildSide = side; }, [side]);
  const list = (builds?.builds ?? []).filter((b) => b.side === side);
  return (
    <>
      <div className="d-metahead">
        <div>
          <div className="d-head">Meta Builds</div>
          <p className="d-note">{builds ? `Complete builds taken from ${builds.players} real players' equipped loadouts in recent matches (same sample as the meta weapons). Updated ${new Date(builds.updated).toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' })}.` : 'No loadouts collected yet.'}</p>
        </div>
        {refresh}
      </div>
      {busyLine}
      <div className="g-chips">
        <Chip on={side === 'pvp'} onClick={() => setSide('pvp')}>PvP</Chip>
        <Chip on={side === 'pve'} onClick={() => setSide('pve')}>PvE</Chip>
      </div>
      {['Titan', 'Hunter', 'Warlock'].map((cls) => {
        const cb = list.filter((b) => b.cls === cls);
        if (!cb.length) return null;
        return (
          <div key={cls}>
            <div className="d-head">{cls}</div>
            {cb.map((b, i) => {
              const key = `${side}${cls}${i}`;
              const ex = b.armor.find((a) => a.exotic);
              return (
                <div key={key} className="d-build">
                  <button type="button" className="top" onClick={() => setOpen(open === key ? undefined : key)}>
                    {b.subclass?.icon && <Pic src={b.subclass.icon} width={42} height={42} />}
                    {ex?.icon && <Pic src={ex.icon} width={42} height={42} />}
                    <span className="words">
                      <b>{b.subclass?.name ?? 'Subclass'}  +  {ex?.name ?? 'no exotic armour'}</b>
                      <span>Super: {b.super[0]?.name ?? 'n/a'}   |   {b.count} player(s) with this build</span>
                    </span>
                    <span className="share">{b.share} % of sampled {cls}s   |   seen in {b.purpose}</span>
                    <span className="arrow">{open === key ? '▴' : '▾'}</span>
                  </button>
                  {open === key && <BuildDetail b={b} ix={ix} openItem={openItem} />}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}

function BuildDetail({ b, ix, openItem }: { b: Build; ix: D2Index; openItem: (it: D2Item) => void }) {
  const item = (p: Plug) => ix.byHash.get(p.hash);
  const weapons = b.weapons.map(item).filter((x): x is D2Item => !!x);
  const srcs = [...new Set([...b.armor, ...b.weapons].map(item).filter((x): x is D2Item => !!x && !!x.source).map((x) => x.source))];
  return (
    <div className="d-builddetail">
      <div className="d-head">Armour</div>
      <div className="d-tiles">{b.armor.map((a) => { const it = item(a); return it ? <ItemCard key={a.hash} it={it} onClick={() => openItem(it)} /> : <PlugPill key={a.hash} d={a} />; })}</div>
      {b.sets.length > 0 && (
        <>
          <div className="d-head">Set bonuses</div>
          {b.sets.map((s) => (
            <div key={s.name}>
              <b className="d-gold">{s.name}  ({s.pieces} pieces)</b>
              {s.bonuses.map((bo) => <p key={bo.name} className="d-line">  {bo.count}-piece  {bo.name}: {bo.desc}</p>)}
            </div>
          ))}
        </>
      )}
      {b.stats.length > 0 && (
        <>
          <div className="d-head">Average stats</div>
          {b.stats.map((s) => <div key={s.name} className="d-stat wide"><span>{s.name}</span><span className="bar"><Bar fraction={s.value / 200} height={6} /></span><b>{s.value}</b></div>)}
        </>
      )}
      {([['Subclass', [...b.super, ...b.abilities, ...b.aspects, ...b.fragments]], ['Mods', b.mods]] as [string, Plug[]][]).map(([title, list]) => list.length > 0 && (
        <div key={title}><div className="d-head">{title}</div><div className="d-plugs">{list.map((p) => <PlugPill key={p.hash} d={p} />)}</div></div>
      ))}
      {weapons.length > 0 && <><div className="d-head">Weapons they run</div><div className="d-tiles">{weapons.map((it) => <ItemCard key={it.hash} it={it} onClick={() => openItem(it)} />)}</div></>}
      {srcs.length > 0 && <><div className="d-head">Where the gear comes from</div>{srcs.map((s) => <p key={s} className="d-line">{s}</p>)}</>}
    </div>
  );
}
